import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Users } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { useBrandImage } from "../../lib/brand";
import { formatEventDate } from "../../lib/format";
import { GoldRule, Swoosh } from "../site/Swoosh";

function CaptionCarousel({ slides = [] }) {
  const [i, setI] = useState(0);
  if (!slides.length) return null;
  const n = slides.length;
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); setI((i + 1) % n); }
    if (e.key === "ArrowLeft") { e.preventDefault(); setI((i - 1 + n) % n); }
  };
  return (
    <div role="region" aria-roledescription="carousel" aria-label="Event highlights" tabIndex={0} onKeyDown={onKey}
      className="on-dark relative z-10 bg-navy px-8 py-6 text-center text-white lg:absolute lg:bottom-14 lg:right-8 lg:w-[30%] lg:min-w-[340px] lg:max-w-[420px] lg:rounded lg:border-t-4 lg:border-gold lg:shadow-xl" data-testid="hero-caption-carousel">
      <p key={i} aria-live="polite" className="font-serif text-lg italic leading-snug motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300" data-testid="hero-caption-text">
        {slides[i].text}
      </p>
      <div className="mt-4 flex justify-center gap-2">
        {slides.map((_, k) => (
          <button key={k} type="button" onClick={() => setI(k)} aria-label={`Show highlight ${k + 1} of ${n}`} aria-current={k === i}
            data-testid={`hero-caption-dot-${k}`}
            className={`h-2.5 rounded-full border border-white transition-[width,background-color] duration-200 ${k === i ? "w-6 bg-white" : "w-2.5 bg-transparent"}`} />
        ))}
      </div>
    </div>
  );
}

const Art = ({ img, className }) => (img ? <img src={img.src} srcSet={img.srcSet} sizes="30vw" alt="" aria-hidden="true" className={className} /> : null);

const SplitTitle = ({ title }) => {
  const words = (title || "").trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  return (
    <h1 className="font-display text-[clamp(2rem,10.5vw,3.75rem)] font-bold uppercase leading-[0.95] text-navy sm:text-6xl lg:text-7xl" data-testid="hero-title">
      {words.join(" ")}{last && <span className="block text-ciarb">{last}</span>}
    </h1>
  );
};

export default function HeroSection({ data }) {
  const { event } = useSite();
  const left = useBrandImage("hero_left");
  const right = useBrandImage("hero_right");
  const banner = useBrandImage("banner");
  const rows = [
    [CalendarDays, formatEventDate(event) + (event.date_confirmed ? "" : " (to be confirmed)"), "hero-event-date"],
    [MapPin, [event.venue_name, event.venue_city].filter(Boolean).join(", "), "hero-event-location"],
    [Users, `${event.attendance_format} Attendance`, "hero-event-format"],
  ];
  return (
    <section className="coastal-sky relative overflow-hidden" data-testid="home-hero">
      <Art img={banner} className="h-40 w-full object-cover object-center sm:h-52 md:hidden" />
      <Art img={left} className="pointer-events-none absolute left-0 top-0 hidden h-full w-[24%] max-w-[360px] object-cover object-right md:block" />
      <Art img={right} className="pointer-events-none absolute right-0 top-0 hidden h-full w-[24%] max-w-[360px] object-cover object-left md:block" />
      <div className="container-site relative pb-20 pt-8 md:pb-28 md:pt-14 lg:pb-36">
        <div className="mx-auto max-w-2xl rounded-md bg-white/90 px-6 py-8 text-center shadow-[0_20px_60px_-30px_rgba(2,27,53,0.55)] backdrop-blur-md sm:px-10 sm:py-10" data-testid="hero-panel">
          <GoldRule className="justify-center" testid="hero-eyebrow">{data.eyebrow}</GoldRule>
          <div className="mt-4"><SplitTitle title={data.title} /></div>
          {data.theme && <p className="mx-auto mt-5 max-w-xl font-serif text-xl leading-snug text-navy sm:text-2xl" data-testid="hero-theme">{data.theme}</p>}
          <p className="mt-5 text-xs font-semibold tracking-[0.3em] text-ocean sm:text-sm">{data.tagline}</p>
          <span className="mx-auto mt-6 block h-px w-40 bg-gold" aria-hidden="true" />
          <ul className="mt-6 space-y-3 text-left sm:mx-auto sm:w-fit">
            {rows.map(([Ico, text, id]) => (
              <li key={id} className="flex items-center gap-3 text-lg text-ink" data-testid={id}><Ico size={22} className="shrink-0 text-ciarb" aria-hidden="true" />{text}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/registration" className="btn-primary px-8" data-testid="hero-register-btn">
              {data.primary_label} <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link to="/event" className="btn-outline-navy px-8" data-testid="hero-event-details-btn">{data.secondary_label}</Link>
          </div>
        </div>
      </div>
      <CaptionCarousel slides={data.slides} />
      <Swoosh />
    </section>
  );
}
